import { EntityType } from '@frontend/common';
import { DefaultPrimaryButton } from '@frontend/components/common';
import { CommonMessage } from '@frontend/lang';
import { OrderExtendedClient } from '@frontend/order/api';
import { OrderStatusBadge } from '@frontend/order/badge';
import { orderRequest } from '@frontend/order/events';
import { ExtendedOrder, Order } from '@frontend/order/types';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { useOrderDetailModal } from '../../applications/shop/orders/order-detail-modal/order-detail-modal.controller';
import { PlatformDetailGrid } from '../../modal/platform-detail-grid/platform-detail-grid.component';
import { PlatformObjectBadge } from '../../modal/platform-object-badge/platform-object-badge.component';
import { ReactiveDetailRegistryEntry, ReactiveDetailView } from '../../modal/reactive-detail-shell/reactive-detail.types';

export const OrderDetailsCard = ({ order }: { order: ExtendedOrder }) => (
    <div className='bg-background-surface outline-border-default inline-flex w-full flex-col items-start justify-start gap-6 self-stretch rounded-xl p-4 outline-1 outline-offset-[-1px]'>
        <div className='text-foreground-default justify-start text-xl font-semibold'>{CommonMessage.DETAIL.DETAILS}</div>
        <PlatformDetailGrid
            object={order}
            cols={2}
            order={['number', 'state', 'user_id', 'product_id', 'quantity', 'spot_id', 'creation_timestamp']}
            keyOverwrite={
                new Map([
                    ['number', CommonMessage.OBJECTS.ORDER.ORDER_NUMBER],
                    ['state', CommonMessage.OBJECTS.COMMON_FIELDS.STATUS],
                    ['user_id', CommonMessage.OBJECTS.USER.DEFAULT],
                    ['product_id', CommonMessage.OBJECTS.PRODUCT.DEFAULT],
                    ['quantity', CommonMessage.OBJECTS.COMMON_FIELDS.AMOUNT],
                    ['spot_id', CommonMessage.OBJECTS.SPOT.DEFAULT],
                    ['creation_timestamp', CommonMessage.OBJECTS.COMMON_FIELDS.CREATED_AT]
                ])
            }
            valueOverwrite={
                new Map<string, (obj: ExtendedOrder) => React.ReactNode>([
                    [
                        'user_id',
                        (obj) =>
                            obj.user_id ? (
                                <PlatformObjectBadge
                                    id={obj.user_id}
                                    entityType={EntityType.USER}
                                    display={obj.user?.name}
                                />
                            ) : (
                                <span>/</span>
                            )
                    ],
                    ['state', (obj) => <OrderStatusBadge status={obj.state} />],
                    [
                        'product_id',
                        (obj) => (
                            <PlatformObjectBadge
                                id={obj.product_id}
                                entityType={EntityType.PRODUCT}
                                display={obj.product?.name}
                            />
                        )
                    ],
                    [
                        'spot_id',
                        (obj) =>
                            obj.spot_id ? (
                                <PlatformObjectBadge
                                    id={obj.spot_id}
                                    entityType={EntityType.SPOT}
                                    display={obj.spot?.name}
                                />
                            ) : (
                                <span>/</span>
                            )
                    ]
                ])
            }
        />
    </div>
);

const OrderHeaderActions = ({ view }: { view: ReactiveDetailView<Order, ExtendedOrder> }) => {
    const { canConvert, convertOrder } = useOrderDetailModal();
    const order = view.extended;
    if (!order || !canConvert(order)) return null;
    return (
        <DefaultPrimaryButton
            size='sm'
            text={
                <FormattedMessage
                    id='platform.shop.orders.order-detail-modal.convert'
                    description='Button that starts converting an eligible shop order into a PuDo transaction'
                    defaultMessage='Convert'
                />
            }
            onClick={() => convertOrder(order)}
        />
    );
};

export const orderReactiveDetail: ReactiveDetailRegistryEntry<Order, ExtendedOrder> = {
    entityType: EntityType.ORDER,
    resolveFunction: orderRequest,
    fetchExtended: ({ object }) => OrderExtendedClient.fetchOrderExtended(object.account_id, object.id),
    modalClassName: 'w-full lg:w-2/3 max-w-[900px]',
    title: ({ object, extended }) => (
        <>
            {CommonMessage.OBJECTS.ORDER.DEFAULT}: {extended?.number ?? object.number ?? object.id}
        </>
    ),
    headerActions: (view) => <OrderHeaderActions view={view} />,
    tabs: [
        {
            id: 'general',
            label: CommonMessage.DETAIL.GENERAL,
            render: (view) => (view.extended ? <OrderDetailsCard order={view.extended} /> : null)
        }
    ]
};
